import posthog from 'posthog-js';
import { POSTHOG_HOST, POSTHOG_KEY, POSTHOG_UI_HOST } from 'common/config/app';
import type { User } from 'common/types/store';

// Guard so posthog.init only runs once even though the provider effect re-runs on user changes.
let initialized = false;
let activeAccountIdentity: string | null = null;

const LEGACY_ALIAS_STORAGE_PREFIX = 'scalar-posthog-email-alias-v1';

const getEmailDomain = (address: string): string => {
  const lastIndex = address.lastIndexOf('@');
  return address.slice(lastIndex + 1);
};

const normalizeEmail = (email?: string): string => email?.trim().toLowerCase() ?? '';

const getPostHogDistinctId = (user: User): string => {
  const email = normalizeEmail(user.email);

  if (email) {
    return email;
  }

  return user.id == null ? '' : String(user.id);
};

const getAccountIdentity = (user: User, distinctId: string): string =>
  user.id == null ? `distinct:${distinctId}` : `user:${user.id}`;

const getUserProperties = (user: User) => {
  const email = normalizeEmail(user.email);

  return {
    email,
    user_id: user.id,
    user_email_domain: email ? getEmailDomain(email) : '',
    is_staff: user.is_staff,
    is_superuser: user.is_superuser,
  };
};

const getLegacyAliasStorageKey = (user: User, distinctId: string): string | null => {
  const legacyDistinctId = user.id == null ? '' : String(user.id);

  if (!legacyDistinctId || !distinctId || distinctId === legacyDistinctId) {
    return null;
  }

  return `${LEGACY_ALIAS_STORAGE_PREFIX}:${legacyDistinctId}:${distinctId}`;
};

const shouldCreateLegacyAlias = (storageKey: string): boolean => {
  try {
    return window.localStorage.getItem(storageKey) !== 'true';
  } catch {
    return true;
  }
};

const markLegacyAliasCreated = (storageKey: string): void => {
  try {
    window.localStorage.setItem(storageKey, 'true');
  } catch {
    // Storage can be unavailable in private contexts; analytics must not break the app.
  }
};

const aliasLegacyUserId = (user: User, distinctId: string): void => {
  const storageKey = getLegacyAliasStorageKey(user, distinctId);
  const legacyDistinctId = user.id == null ? '' : String(user.id);

  // Only alias a browser that was already identified by the legacy numeric user ID.
  // New anonymous sessions and account switches should simply identify as the email.
  if (!storageKey || posthog.get_distinct_id() !== legacyDistinctId || !shouldCreateLegacyAlias(storageKey)) {
    return;
  }

  posthog.alias(distinctId, legacyDistinctId);
  markLegacyAliasCreated(storageKey);
};

// Initializes PostHog (product analytics + session replay) and identifies the current user.
// Session recording is enabled by default; it still requires the project-level
// "Record user sessions" toggle to be on in PostHog.
export const initPostHog = (user: User | null): void => {
  if (!POSTHOG_KEY) return;

  if (!initialized) {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST, // api_host is our reverse proxy; keep generated links (toolbar, etc.) pointed at real PostHog
      capture_pageview: true,
      disable_session_recording: false, // session replay ON
      capture_heatmaps: true, // heatmaps ($heatmap_data) ON; clickmaps use autocapture (on by default)
      capture_exceptions: true, // frontend error tracking ($exception) ON — links to the session automatically
    });
    initialized = true;
    // Module import does not attach to window; expose the instance for debugging/console access.
    (window as unknown as { posthog: typeof posthog }).posthog = posthog;
  }

  if (!user) {
    stopPostHog();
    return;
  }

  const distinctId = getPostHogDistinctId(user);

  if (!distinctId) {
    stopPostHog();
    return;
  }

  const accountIdentity = getAccountIdentity(user, distinctId);

  // Reset only for an actual Scalar account switch. An email change updates the
  // PostHog distinct ID for the same account without interrupting its session replay.
  if (activeAccountIdentity && activeAccountIdentity !== accountIdentity) {
    posthog.reset(true);
  }

  posthog.opt_in_capturing({ captureEventName: false });
  posthog.startSessionRecording();
  aliasLegacyUserId(user, distinctId);
  posthog.identify(distinctId, getUserProperties(user));
  activeAccountIdentity = accountIdentity;
};

// Stops capture and resets identity when there is no authenticated Scalar user.
export const stopPostHog = (): void => {
  if (!POSTHOG_KEY || !initialized) return;
  activeAccountIdentity = null;
  posthog.reset(true);
  posthog.opt_out_capturing();
  posthog.stopSessionRecording();
};

// Headers that link a backend request to the current browser session + person. The Django
// ScalarPosthogContextMiddleware reads these and tags every server-side event/exception/log
// with the same session + person, so backend errors resolve to a replay + user.
export const getPostHogTrackingHeaders = (): Record<string, string> => {
  if (!POSTHOG_KEY || !initialized) return {};

  const headers: Record<string, string> = {};
  const sessionId = posthog.get_session_id();
  const distinctId = posthog.get_distinct_id();
  if (sessionId) headers['X-POSTHOG-SESSION-ID'] = sessionId;
  if (distinctId) headers['X-POSTHOG-DISTINCT-ID'] = distinctId;
  return headers;
};
